import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from './pilot-ui/table';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';
import { useDevices } from '../hooks/useDevices';
import { useFirestoreEnrichment } from '../hooks/useFirestoreEnrichment';
import { formatDeviceLabel, buildUserDeviceIndex } from './DeviceSelector';
import { AnalysisMethod, AnalysisMethodDevice } from '../types';
import Button from './ui/Button';
import { Checkbox } from './pilot-ui/checkbox';
import { resolveReturnFocus } from './returnFocus';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './pilot-ui/collapsible';

const STANDARD_KEY = 'standard_daily_v1';

// Side effects that homeowners see / that mutate device state — enabling them
// needs an explicit warning (Plan 80).
const HOMEOWNER_VISIBLE = new Set(['firestore_filter_health', 'homeowner_alerts']);
const BACKFILL_SENSITIVE = new Set(['filter_baseline_mutation', 'daily_filter_health_metrics']);

const ALL_SIDE_EFFECTS = [
  'filter_baseline_mutation',
  'daily_filter_health_metrics',
  'firestore_filter_health',
  'homeowner_alerts',
];

const base = () => `${config.devicesApiUrl}/analysis-methods`;
const manageButtonId = (key: string) => `analysis-method-manage-${key}`;

const AnalysisMethodsPanel: React.FC = () => {
  const { hasPermission } = useAuth();
  const confirm = useConfirm();
  const { devices } = useDevices();
  const { enrichmentMap } = useFirestoreEnrichment();
  const userDeviceIndex = useMemo(() => buildUserDeviceIndex(enrichmentMap), [enrichmentMap]);
  const labelFor = useCallback(
    (dev: any) => formatDeviceLabel(dev, enrichmentMap[dev.name], userDeviceIndex),
    [enrichmentMap, userDeviceIndex],
  );
  const canManage = hasPermission('manage_analysis_runs');

  const [methods, setMethods] = useState<AnalysisMethod[]>([]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [methodDevices, setMethodDevices] = useState<AnalysisMethodDevice[]>([]);
  const [addIds, setAddIds] = useState<number[]>([]);
  const [addKickoff, setAddKickoff] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadMethods = useCallback(async () => {
    try {
      const res = await axiosInstance.get<AnalysisMethod[]>(base());
      setMethods(res.data);
    } catch (e) {
      console.error('load analysis methods', e);
      setError('Failed to load analysis methods');
    }
  }, []);

  useEffect(() => {
    if (canManage && open) loadMethods();
  }, [canManage, open, loadMethods]);

  const loadDevices = useCallback(async (key: string) => {
    try {
      const res = await axiosInstance.get<AnalysisMethodDevice[]>(
        `${base()}/${encodeURIComponent(key)}/devices`);
      setMethodDevices(res.data);
    } catch (e) {
      console.error('load method devices', e);
      setError('Failed to load assigned devices');
    }
  }, []);

  const openDialog = async (key: string) => {
    setActiveKey(key);
    setAddIds([]);
    await loadDevices(key);
  };

  const refresh = async () => {
    await loadMethods();
    if (activeKey) await loadDevices(activeKey);
  };

  const addDevices = async () => {
    if (!activeKey || addIds.length === 0) return;
    setBusy(true);
    try {
      await axiosInstance.post(`${base()}/${encodeURIComponent(activeKey)}/devices`,
        { device_ids: addIds, kickoff: addKickoff });
      setAddIds([]);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to add devices');
    } finally {
      setBusy(false);
    }
  };

  const kickoff = async (deviceId: number) => {
    if (!activeKey) return;
    setBusy(true);
    try {
      await axiosInstance.post(
        `${base()}/${encodeURIComponent(activeKey)}/devices/${deviceId}/kickoff`, {});
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Kickoff failed');
    } finally {
      setBusy(false);
    }
  };

  const rebuild = async (d: AnalysisMethodDevice) => {
    if (!activeKey) return;
    const ok = await confirm({
      title: 'Rebuild analysis',
      message: `Delete ${d.processed_days} processed day(s) and model state for `
        + `${d.device_name} under "${activeKey}", then re-queue every day from the filter `
        + `install date. This cannot be undone. Continue?`,
      confirmLabel: 'Rebuild',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await axiosInstance.post(
        `${base()}/${encodeURIComponent(activeKey)}/devices/${d.device_id}/rebuild`, {});
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Rebuild failed');
    } finally {
      setBusy(false);
    }
  };

  const setDefault = async (deviceId: number, key: string) => {
    setBusy(true);
    try {
      await axiosInstance.put(
        `${base()}/${encodeURIComponent(key)}/devices/${deviceId}/default`, {});
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Set default failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleSideEffect = async (d: AnalysisMethodDevice, seKey: string, enable: boolean) => {
    if (!activeKey) return;
    if (enable) {
      let warn = '';
      if (HOMEOWNER_VISIBLE.has(seKey)) {
        warn = `"${seKey}" is homeowner-visible — enabling it sends app-facing data / alerts `
          + `for ${d.device_name}. `;
      } else if (BACKFILL_SENSITIVE.has(seKey)) {
        warn = `"${seKey}" mutates production device state. Enable only after a completed `
          + `backfill, or filter health may jump for ${d.device_name}. `;
      }
      if (warn) {
        const ok = await confirm({ title: 'Enable production side effect', message: warn + 'Continue?',
          confirmLabel: 'Enable' });
        if (!ok) return;
      }
    }
    setBusy(true);
    try {
      await axiosInstance.put(
        `${base()}/${encodeURIComponent(activeKey)}/devices/${d.device_id}/side-effects/${seKey}`,
        { enabled: enable });
      await refresh();
    } catch (e: any) {
      // 409 => another analysis owns this side effect; confirm transfer.
      if (e?.response?.status === 409) {
        const owner = e.response.data?.current_owner;
        const ok = await confirm({
          title: 'Transfer side effect',
          message: `"${seKey}" is currently owned by "${owner}" for ${d.device_name}. `
            + `Transfer it to "${activeKey}"? The current owner stops driving this channel.`,
          confirmLabel: 'Transfer',
        });
        if (ok) {
          try {
            await axiosInstance.put(
              `${base()}/${encodeURIComponent(activeKey)}/devices/${d.device_id}/side-effects/${seKey}`,
              { enabled: true, confirm_transfer: true });
            await refresh();
          } catch (e2: any) {
            setError(e2?.response?.data?.message || 'Transfer failed');
          }
        }
      } else {
        setError(e?.response?.data?.message || 'Side-effect toggle failed');
      }
    } finally {
      setBusy(false);
    }
  };

  // FS-535: pause/resume the assignment itself. Disabling stops ALL
  // processing for this device+method (nightly runs, filter-change
  // reprocessing, kickoff) and hides it from the analysis selector;
  // historical rows and model state are kept, and re-enabling backfills the
  // missed days automatically.
  const setAssignmentEnabled = async (d: AnalysisMethodDevice, enable: boolean) => {
    if (!activeKey) return;
    if (!enable) {
      if (d.is_default) { setError('Set another default before disabling this assignment.'); return; }
      if (d.side_effects.some((s) => s.enabled)) {
        setError('Disable/transfer this assignment\'s side effects before disabling it.'); return;
      }
      const ok = await confirm({
        title: 'Disable assignment',
        message: `Disable "${activeKey}" for ${d.device_name}. All processing for it stops — `
          + `nightly runs, filter-change reprocessing, kickoff — and it disappears from the `
          + `analysis selector. Historical rows and model state are kept; re-enabling resumes `
          + `processing and backfills the missed days.`,
        confirmLabel: 'Disable',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await axiosInstance.put(
        `${base()}/${encodeURIComponent(activeKey)}/devices/${d.device_id}/enabled`,
        { enabled: enable });
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || (enable ? 'Enable failed' : 'Disable failed'));
    } finally {
      setBusy(false);
    }
  };

  const removeOrDelete = async (d: AnalysisMethodDevice, purge: boolean) => {
    if (!activeKey) return;
    if (d.is_default) { setError('Set another default before removing this assignment.'); return; }
    if (d.side_effects.some((s) => s.enabled)) {
      setError('Disable/transfer this assignment\'s side effects before removing it.'); return;
    }
    const ok = await confirm({
      title: purge ? 'Delete assignment + data' : 'Remove from method',
      message: purge
        ? `Delete the "${activeKey}" assignment for ${d.device_name}, its `
          + `${d.processed_days} analysis row(s), side-effect rows, and model state. Cannot be undone.`
        : `Disable the "${activeKey}" assignment for ${d.device_name}. Historical rows and model `
          + `state are kept.`,
      confirmLabel: purge ? 'Delete' : 'Remove',
      tone: purge ? 'danger' : 'primary',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await axiosInstance.delete(
        `${base()}/${encodeURIComponent(activeKey)}/devices/${d.device_id}`,
        { params: purge ? { purge: 'true' } : {} });
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  if (!canManage) return null;

  const activeMethod = methods.find((m) => m.analysis_key === activeKey) || null;
  const assignedIds = new Set(methodDevices.map((d) => d.device_id));
  const addableDevices = devices.filter((dev) => !assignedIds.has(dev.id));

  return (
    <div className="mt-6 border-t pt-4">
      <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger render={<Button variant="secondary" title={open ? 'Hide the analysis methods and their device assignments.' : 'Show the analysis methods and manage device assignments.'} />}>
        Analysis Methods (admin)
      </CollapsibleTrigger>
      <p className="text-xs text-muted-foreground">Review available methods and manage their device assignments.</p>
      <CollapsibleContent className="mt-3 space-y-3">
          {error && <div role="alert" className="border border-status-error-border bg-status-error text-status-error-foreground p-2 rounded-sm text-sm">{error}</div>}

          <div className="overflow-x-auto"><Table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-1">Method</th>
                <th>Status</th>
                <th>Handler</th>
                <th>New-device default</th>
                <th>Devices</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {methods.map((m) => (
                <tr key={m.analysis_key} className="border-b">
                  <td className="py-1">
                    <div className="font-medium">{m.display_name}</div>
                    <div className="text-xs text-gray-600 font-mono">{m.analysis_key}</div>
                  </td>
                  <td>{m.status}{!m.runnable && <span className="text-amber-800"> (handler not deployed)</span>}</td>
                  <td className="font-mono text-xs">{m.handler_key}</td>
                  <td>{m.is_default_for_new_devices ? 'yes' : '—'}</td>
                  <td>{m.assigned_device_count}</td>
                  <td className="text-right">
                    {m.has_device_dialog && m.runnable && (
                      <Button
                        id={manageButtonId(m.analysis_key)}
                        variant="link"
                        size="sm"
                        onClick={() => openDialog(m.analysis_key)}
                        title="Open the device list for this method below: add devices, kick off or rebuild their days, set defaults and side effects."
                      >
                        Manage devices
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table></div>

          {activeMethod && (
            <div className="border rounded-md p-3 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">Manage devices — {activeMethod.display_name}</h4>
                <Button variant="secondary" size="sm" onClick={() => {
                  resolveReturnFocus(document.getElementById(manageButtonId(activeMethod.analysis_key)))?.focus();
                  setActiveKey(null);
                  setMethodDevices([]);
                }}
                        title="Close this device list. Nothing changes; open it again with Manage devices.">close</Button>
              </div>

              {/* Add devices */}
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <fieldset className="min-w-64 max-h-36 overflow-y-auto rounded-lg border border-control-border p-2 text-sm">
                  <legend className="font-medium">Devices to add</legend>
                  {addableDevices
                    .slice()
                    .sort((a, b) => labelFor(a).localeCompare(labelFor(b)))
                    .map((dev) => (
                      <label key={dev.id} className="flex items-center gap-2 py-1">
                        <Checkbox className="size-6" checked={addIds.includes(dev.id)} onCheckedChange={(checked) => setAddIds((ids) => checked ? [...ids, dev.id] : ids.filter((id) => id !== dev.id))} title={`Include ${labelFor(dev)} in this assignment`} />
                        {labelFor(dev)}
                      </label>
                    ))}
                  {addableDevices.length === 0 && <p className="text-muted-foreground">No unassigned devices available.</p>}
                </fieldset>
                <label className="text-xs flex items-center gap-1">
                  <Checkbox className="size-6" checked={addKickoff} onCheckedChange={setAddKickoff} title="Queue missing days for processing after assigning devices" />
                  kickoff missing days
                </label>
                <Button variant="primary" size="sm" disabled={busy || addIds.length === 0} onClick={addDevices}
                        title="Assign the devices selected in the list to this method. With kickoff checked, their missing days are queued for processing right away.">
                  Add {addIds.length || ''}
                </Button>
              </div>

              <div className="overflow-x-auto"><Table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-1">Device</th>
                    <th>Enabled</th>
                    <th>Default</th>
                    <th>Side effects</th>
                    <th>Days</th>
                    <th>Latest</th>
                    <th>Model</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {methodDevices.map((d) => (
                    <tr key={d.device_id}
                        className={`border-b align-top ${d.enabled ? '' : 'opacity-60 bg-gray-100'}`}>
                      <td className="py-1">{d.device_name}</td>
                      <td>
                        {d.enabled ? 'yes' : (
                          <span className="inline-block px-1.5 py-0.5 rounded-sm border border-status-error-border bg-status-error text-status-error-foreground font-semibold uppercase">
                            disabled
                          </span>
                        )}
                      </td>
                      <td>{d.is_default ? 'yes' : '—'}</td>
                      <td>
                        <div className="flex flex-col gap-0.5">
                          {ALL_SIDE_EFFECTS.map((se) => {
                            const on = d.side_effects.some((s) => s.side_effect_key === se && s.enabled);
                            return (
                              <label key={se} className="flex items-center gap-1">
                                {/* A disabled assignment never runs, so a side effect
                                    enabled on it would silently kill that channel —
                                    the API refuses it and the checkbox stays locked. */}
                                <Checkbox className="size-6" checked={on} disabled={busy || !d.enabled}
                                       title={d.enabled ? `Enable or disable ${se} for ${d.device_name}`
                                         : 'Enable the assignment before changing its side effects.'}
                                       onCheckedChange={(checked) => toggleSideEffect(d, se, checked)} />
                                <span className={HOMEOWNER_VISIBLE.has(se) ? 'text-amber-700' : ''}>{se}</span>
                              </label>
                            );
                          })}
                        </div>
                      </td>
                      <td>{d.processed_days}</td>
                      <td>
                        {d.latest_processed_date || '—'}
                        {d.latest_error && (
                          <div className="text-status-error-foreground" title={d.latest_error.error_message}>err {d.latest_error.date}</div>
                        )}
                      </td>
                      <td>{d.model?.updated_through_date || '—'}</td>
                      <td className="text-right whitespace-nowrap">
                        <div className="flex flex-col gap-1 items-end">
                          {/* The API refuses kickoff/rebuild on a disabled
                              assignment; keep the buttons locked with it. */}
                          <Button variant="link" className="min-h-6" disabled={busy || !d.enabled} onClick={() => kickoff(d.device_id)}
                                  title={d.enabled ? 'Queue every day this device has not yet been processed under this method. Days already done are left alone.' : 'Enable the assignment first.'}>kickoff</Button>
                          <Button variant="link" className="min-h-6" disabled={busy || !d.enabled} onClick={() => rebuild(d)}
                                  title={d.enabled ? 'Delete this device\'s processed days and model state under this method and process every day again from the filter install date. Asks first.' : 'Enable the assignment first.'}>rebuild</Button>
                          {/* Locked on disabled rows: enable first, then
                              promote — the API guards the one-step promote
                              and its refusals surface verbatim if reached. */}
                          {!d.is_default && (
                            <Button variant="link" disabled={busy || !d.enabled}
                                    title={d.enabled ? 'Make this method the default analysis for this device, in place of the one it uses now.' : 'Enable the assignment first.'}
                                    onClick={() => setDefault(d.device_id, activeMethod.analysis_key)}>make default</Button>
                          )}
                          {d.is_default && (
                            <Button variant="link" disabled={busy}
                                    onClick={() => setDefault(d.device_id, STANDARD_KEY)}
                                    title="Make the standard daily analysis this device's default again, in place of this method.">restore standard</Button>
                          )}
                          {d.enabled ? (
                            <Button variant="link" disabled={busy}
                                    onClick={() => setAssignmentEnabled(d, false)}
                                    title="Pause this method for this device: all processing stops and it leaves the analysis selector. History is kept. Asks first.">disable</Button>
                          ) : (
                            <Button variant="link" disabled={busy}
                                    onClick={() => setAssignmentEnabled(d, true)}
                                    title="Resume this method for this device. The days it missed while disabled are processed automatically.">enable</Button>
                          )}
                          <a href={`/devices/${d.device_id}/analysis?compare=${encodeURIComponent(activeMethod.analysis_key)}`}
                            className="text-primary underline" title="Compare this method beside this Clip's default analysis">Open comparison</a>
                          <Button variant="danger" size="sm" disabled={busy} onClick={() => removeOrDelete(d, false)}
                                  title="Take this device off this method. Its processed days and model state are kept. Asks first.">remove</Button>
                          <Button variant="danger" size="sm" disabled={busy} onClick={() => removeOrDelete(d, true)}
                                  title="Take this device off this method and delete its processed days, side-effect rows and model state for good. Asks first.">delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {methodDevices.length === 0 && (
                    <tr><td colSpan={8} className="py-2 text-gray-600">No devices assigned.</td></tr>
                  )}
                </tbody>
              </Table></div>
            </div>
          )}
      </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default AnalysisMethodsPanel;
